/*
 * -----------------------------------------------------------------------
 *  <copyright file="audit-log/item-types.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React from 'react';
import { AuditLogChangeHistoryGrid } from '@pages/admin/audit-log/types/audit-log-change-history';
import { CM2ActionRecordChangeGrid } from '@pages/admin/audit-log/types/cm2-action';
import { CM2ObservationRecordChangeGrid } from '@pages/admin/audit-log/types/cm2-observation';
import Comment from '@pages/admin/audit-log/types/comment';
import ControlsHubConfigPanelAuditLogGrid from '@pages/admin/audit-log/types/controls-hub-config-panel';
import ControlsHubConfigWidgetAuditLogGrid from '@pages/admin/audit-log/types/controls-hub-config-widget';
import ControlsHubWidgetAuditLogGrid from '@pages/admin/audit-log/types/controls-hub-widget';
import { CustomReportTemplateRecordChangeGrid } from '@pages/admin/audit-log/types/custom-report-template';
import { DataViewConfigurationChangeGrid } from '@pages/admin/audit-log/types/data-view-configuration';
import { DataViewFilterChangeGrid } from '@pages/admin/audit-log/types/data-view-filter';
import { FunctionalDataRviewsRecordChangeGrid } from '@pages/admin/audit-log/types/functional-data-reviews';
import { GeneratedReportRecordChangesGridPBI } from '@pages/admin/audit-log/types/generated-report-pbi';
import { LibraryFunctionDataReviewChangeGrid } from '@pages/admin/audit-log/types/library-function-data-review';
import { OpraReportTemplateRecordChangeGridPBI } from '@pages/admin/audit-log/types/opra-report-template';
import { ReportSignatoryRecordChangeGridPBI } from '@pages/admin/audit-log/types/report-signatory-pbi';
import RiskAssessmentTag from '@pages/admin/audit-log/types/risk-assessment-tag';
import { RiskDashboardSetupGrid } from '@pages/admin/audit-log/types/risk-dashboard-setup';
import { RiskReviewRecordChangeGrid } from '@pages/admin/audit-log/types/risk-review';
import { RiskReviewAttendeesRecordChangeGrid } from '@pages/admin/audit-log/types/risk-review-attendees';
import { SMDataListingsConfigRecordChangeGrid } from '@pages/admin/audit-log/types/sm-data-listings-config';
import { SMOverviewGridConfigRecordChangeGrid } from '@pages/admin/audit-log/types/sm-overview-grid-config';
import SmReviews from '@pages/admin/audit-log/types/sm-reviews';
import { SMSubjectProfileDetailsConfigRecordChangeGrid } from '@pages/admin/audit-log/types/sm-subject-profile-details-config';
import SmTagInstance from '@pages/admin/audit-log/types/sm-tag-instance';
import StudyDashboardConfigAuditLogGrid from '@pages/admin/audit-log/types/study-dashboard-config';
import StudyDashboardConfigPanelAuditLogGrid from '@pages/admin/audit-log/types/study-dashboard-config-panel';
import StudyDashboardConfigWidgetAuditLogGrid from '@pages/admin/audit-log/types/study-dashboard-config-widget';
import { StudyHubSiteFieldsChangeGrid } from '@pages/admin/audit-log/types/study-hub-site-fields';
import { StudyHubStudyFieldsChangeGrid } from '@pages/admin/audit-log/types/study-hub-study-fieelds';
import SubjectActionsAuditLogGrid from '@pages/admin/audit-log/types/subject-actions-audit-log-grid';
import SubjectDataListingReviewInstances from '@pages/admin/audit-log/types/subject-data-listing-review-instances';
import SubjectObservationsAuditLogGrid from '@pages/admin/audit-log/types/subject-observations-audit-log-grid';
import SubjectReviewInstances from '@pages/admin/audit-log/types/subject-review-instances';
import SubjectReviewLogsAuditLogGrid from '@pages/admin/audit-log/types/subject-review-logs-audit-log-grid';
import Tag from '@pages/admin/audit-log/types/tag';
import { WidgetAuditLogGrid } from '@pages/admin/audit-log/types/widget';
import { CM2ReviewLogRecordChangeGrid } from '../types/cm2-review-log';
import { SMReviewPlanRecordChangeGrid } from '../types/sm-review-plan';
import { SMReviewType } from '../types/sm-review-type';
import {
    GET_AUDIT_HISTORY_CRITICAL_VARIABLE,
    GET_AUDIT_HISTORY_EVALUATION_MODEL,
    GET_AUDIT_HISTORY_ISMP,
    GET_AUDIT_HISTORY_LIBRARY_CONTROL,
    GET_AUDIT_HISTORY_LIBRARY_RISK,
    GET_AUDIT_HISTORY_NAMED_ITEMS,
    GET_AUDIT_HISTORY_ORGANISATION,
    GET_AUDIT_HISTORY_ORGANISATION_INDICATION,
    GET_AUDIT_HISTORY_ORGANISATION_MEMBER,
    GET_AUDIT_HISTORY_ORGANISATION_PRODUCT,
    GET_AUDIT_HISTORY_ORGANISATION_PROGRAM,
    GET_AUDIT_HISTORY_ORGANISATION_ROLE,
    GET_AUDIT_HISTORY_PANEL,
    GET_AUDIT_HISTORY_PLUGIN,
    GET_AUDIT_HISTORY_REPORT,
    GET_AUDIT_HISTORY_REPORT_SIGNATORY,
    GET_AUDIT_HISTORY_RISK,
    GET_AUDIT_HISTORY_RISK_EVALUATION,
    GET_AUDIT_HISTORY_RISK_OBSERVATION,
    GET_AUDIT_HISTORY_RISK_ACTION,
    GET_AUDIT_HISTORY_STUDY,
    GET_AUDIT_HISTORY_STUDY_COHORT,
    GET_AUDIT_HISTORY_STUDY_CONTROL,
    GET_AUDIT_HISTORY_STUDY_CRF,
    GET_AUDIT_HISTORY_STUDY_MEMBER,
    GET_AUDIT_HISTORY_STUDY_TREATMENT_ARM,
    GET_AUDIT_HISTORY_SVT,
    GET_AUDIT_HISTORY_USER,
    GET_AUDIT_HISTORY_CM_OBSERVATION,
    GET_AUDIT_HISTORY_CM_ACTION,
    GET_AUDIT_HISTORY_STUDY_SUBJECT_VISIT,
    GET_AUDIT_HISTORY_DATA_REVIEW_TABLE,
    GET_AUDIT_HISTORY_DATA_SET,
    GET_AUDIT_HISTORY_DATA_SET_REVIEW,
    GET_AUDIT_HISTORY_EXTRA_DATA,
    GET_AUDIT_HISTORY_SITE_REVIEW,
    GET_AUDIT_HISTORY_SYSTEM_ORGANISATION,
    GET_AUDIT_HISTORY_STUDY_DASHBOARD_SETUP,
    GET_AUDIT_HISTORY_STUDY_ASSESSMENT,
    GET_AUDIT_HISTORY_REPORT_DEFINITION,
    GET_AUDIT_HISTORY_DATA_VIEW_CONFIGURATION,
    GET_AUDIT_HISTORY_DATA_VIEW_FILTER,
    GET_AUDIT_HISTORY_STUDY_DASHBOARD_CONFIG,
    GET_AUDIT_HISTORY_STUDY_DASHBOARD_CONFIG_PANEL,
    GET_AUDIT_HISTORY_STUDY_DASHBOARD_CONFIG_WIDGET,
    GET_AUDIT_HISTORY_WIDGET,
    GET_AUDIT_LOG_CHANGE_HISTORY,
    GET_AUDIT_HISTORY_FUNCTIONAL_DATA_REVIEW,
    GET_AUDIT_HISTORY_LIBRARY_FUNCTIONAL_DATA_REVIEW,
    GET_AUDIT_HISTORY_CONTROLS_HUB_CONFIG_WIDGET,
    GET_AUDIT_HISTORY_CONTROLS_HUB_WIDGET,
    GET_AUDIT_HISTORY_CONTROLS_HUB_CONFIG_PANEL,
    GET_AUDIT_HISTORY_RISK_COMMENT,
    GET_AUDIT_HISTORY_RISK_COMMENT_REPLY,
    GET_AUDIT_HISTORY_CTQ_COMMENT,
    GET_AUDIT_HISTORY_CTQ_COMMENT_REPLY,
} from 'queries/audit';
import { AuditDataType } from 'types/graphql-global-types';
import { CMActionRecordChangeGrid } from 'views/pages/admin/audit-log/types/cm-actions';
import { CMObservationRecordChangeGrid } from 'views/pages/admin/audit-log/types/cm-observations';
import { CriticalVariableRecordChangeGrid } from 'views/pages/admin/audit-log/types/critical-variables';
import { DashboardSetupChangeGrid } from 'views/pages/admin/audit-log/types/dashboard-setup';
import { DataReviewTableRecordChangeGrid } from 'views/pages/admin/audit-log/types/data-review-table';
import { DataSetRecordChangeGrid } from 'views/pages/admin/audit-log/types/data-set';
import { DataSetReviewRecordChangeGrid } from 'views/pages/admin/audit-log/types/data-set-review';
import { ExtraDataFieldRecordChangeGrid } from 'views/pages/admin/audit-log/types/extra-data-field';
import { IsmpRecordChangeGrid } from 'views/pages/admin/audit-log/types/ismp';
import { LibraryControlRecordChangeGrid } from 'views/pages/admin/audit-log/types/library-controls';
import { LibraryRiskRecordChangeGrid } from 'views/pages/admin/audit-log/types/library-risk';
import { NamedItemRecordChangeGrid } from 'views/pages/admin/audit-log/types/named-item';
import { OrganisationRecordChangeGrid } from 'views/pages/admin/audit-log/types/organisation';
import { OrganisationIndicationRecordChangeGrid } from 'views/pages/admin/audit-log/types/organisation-indication';
import { OrganisationMemberRecordChangeGrid } from 'views/pages/admin/audit-log/types/organisation-member';
import { OrganisationProductRecordChangeGrid } from 'views/pages/admin/audit-log/types/organisation-product';
import { OrganisationProgramRecordChangeGrid } from 'views/pages/admin/audit-log/types/organisation-program';
import { OrganisationRoleRecordChangeGrid } from 'views/pages/admin/audit-log/types/organisation-role';
import { PanelRecordChangeGrid } from 'views/pages/admin/audit-log/types/panel';
import { PluginRecordChangeGrid } from 'views/pages/admin/audit-log/types/plugin';
import { ReportRecordChangeGrid } from 'views/pages/admin/audit-log/types/report';
import { ReportDefinitionChangeGrid } from 'views/pages/admin/audit-log/types/report-definition';
import { ReportSignatoryRecordChangeGrid } from 'views/pages/admin/audit-log/types/report-signatory';
import { RiskActionRecordChangeGrid } from 'views/pages/admin/audit-log/types/risk-action';
import { RiskEvaluationRecordChangeGrid } from 'views/pages/admin/audit-log/types/risk-evaluation';
import { RiskEvaluationModelRecordChangeGrid } from 'views/pages/admin/audit-log/types/risk-evaluation-model';
import { RiskObservationRecordChangeGrid } from 'views/pages/admin/audit-log/types/risk-observation';
import { RiskRecordChangeGrid } from 'views/pages/admin/audit-log/types/risks';
import { SiteReviewRecordChangeGrid } from 'views/pages/admin/audit-log/types/site-review';
import { StudyRecordChangeGrid } from 'views/pages/admin/audit-log/types/study';
import { StudyAssessmentRecordChangeGrid } from 'views/pages/admin/audit-log/types/study-assessment';
import { StudyCohortRecordChangeGrid } from 'views/pages/admin/audit-log/types/study-cohort';
import { StudyControlRecordChangeGrid } from 'views/pages/admin/audit-log/types/study-control';
import { StudyCrfRecordChangeGrid } from 'views/pages/admin/audit-log/types/study-crf';
import { StudyMemberRecordChangeGrid } from 'views/pages/admin/audit-log/types/study-member';
import { StudySubjectVisitRecordChangeGrid } from 'views/pages/admin/audit-log/types/study-subject-visit';
import { StudySvtRecordChangeGrid } from 'views/pages/admin/audit-log/types/study-svt';
import { StudyTreatmentArmRecordChangeGrid } from 'views/pages/admin/audit-log/types/study-treatment-arm';
import { SystemOrganisationRecordChangeGrid } from 'views/pages/admin/audit-log/types/system-organisation';
import { UserRecordChangeGrid } from 'views/pages/admin/audit-log/types/user';

const AuditItemTypes = {
    [AuditDataType.CRITICAL_VARIABLE]: {
        query: GET_AUDIT_HISTORY_CRITICAL_VARIABLE,
        response: 'criticalVariableHistory',
        grid: (props: any) => <CriticalVariableRecordChangeGrid {...props} />,
    },
    [AuditDataType.FUNCTIONAL_DATA_REVIEW]: {
        query: GET_AUDIT_HISTORY_FUNCTIONAL_DATA_REVIEW,
        response: 'functionalDataReviewHistory',
        grid: (props: any) => <FunctionalDataRviewsRecordChangeGrid {...props} />,
    },
    [AuditDataType.PBI_CUSTOM_REPORT_TEMPLATE]: {
        query: null,
        response: null,
        grid: (props: any) => <CustomReportTemplateRecordChangeGrid {...props} />,
    },
    [AuditDataType.PBIRAM_REPORT_SIGNATORY]: {
        query: null,
        response: null,
        grid: (props: any) => <ReportSignatoryRecordChangeGridPBI {...props} />,
    },
    [AuditDataType.PBI_REPORT]: {
        query: null,
        response: null,
        grid: (props: any) => <GeneratedReportRecordChangesGridPBI {...props} />,
    },
    [AuditDataType.PBICM_REPORT_SIGNATORY]: {
        query: null,
        response: null,
        grid: (props: any) => <ReportSignatoryRecordChangeGridPBI {...props} />,
    },
    [AuditDataType.PBICM_2_REPORT_SIGNATORY]: {
        query: null,
        response: null,
        grid: (props: any) => <ReportSignatoryRecordChangeGridPBI {...props} />,
    },
    [AuditDataType.PBISM_REPORT_SIGNATORY]: {
        query: null,
        response: null,
        grid: (props: any) => <ReportSignatoryRecordChangeGridPBI {...props} />,
    },
    [AuditDataType.PBIOPRA_REPORT_TEMPLATE]: {
        query: null,
        response: null,
        grid: (props: any) => <OpraReportTemplateRecordChangeGridPBI {...props} />,
    },
    [AuditDataType.RISKS]: {
        query: GET_AUDIT_HISTORY_RISK,
        response: 'riskHistory',
        grid: (props: any) => <RiskRecordChangeGrid {...props} />,
    },
    [AuditDataType.PBI_RISK_REVIEW_ATTENDEE]: {
        query: null,
        response: null,
        grid: (props: any) => <RiskReviewAttendeesRecordChangeGrid {...props} />,
    },
    [AuditDataType.USERS]: {
        query: GET_AUDIT_HISTORY_USER,
        response: 'userHistory',
        grid: (props: any) => <UserRecordChangeGrid {...props} />,
    },
    [AuditDataType.LIBRARY_CONTROL]: {
        query: GET_AUDIT_HISTORY_LIBRARY_CONTROL,
        response: 'libraryControlHistory',
        grid: (props: any) => <LibraryControlRecordChangeGrid {...props} />,
    },
    [AuditDataType.LIBRARY_RISK]: {
        query: GET_AUDIT_HISTORY_LIBRARY_RISK,
        response: 'libraryRiskHistory',
        grid: (props: any) => <LibraryRiskRecordChangeGrid {...props} />,
    },
    [AuditDataType.ISMP]: {
        query: GET_AUDIT_HISTORY_ISMP,
        response: 'ismpHistory',
        grid: (props: any) => <IsmpRecordChangeGrid {...props} />,
    },
    [AuditDataType.NAMED_ITEM]: {
        query: GET_AUDIT_HISTORY_NAMED_ITEMS,
        response: 'namedItemHistory',
        grid: (props: any) => <NamedItemRecordChangeGrid {...props} />,
    },
    [AuditDataType.ORGANISATION]: {
        query: GET_AUDIT_HISTORY_ORGANISATION,
        response: 'organisationHistory',
        grid: (props: any) => <OrganisationRecordChangeGrid {...props} />,
    },
    [AuditDataType.SYSTEM_ORGANISATION]: {
        query: GET_AUDIT_HISTORY_SYSTEM_ORGANISATION,
        response: 'systemOrganisationHistory',
        grid: (props: any) => <SystemOrganisationRecordChangeGrid {...props} />,
    },
    [AuditDataType.ORGANISATION_ROLE]: {
        query: GET_AUDIT_HISTORY_ORGANISATION_ROLE,
        response: 'organisationRoleHistory',
        grid: (props: any) => <OrganisationRoleRecordChangeGrid {...props} />,
    },
    [AuditDataType.PLUGIN]: {
        query: GET_AUDIT_HISTORY_PLUGIN,
        response: 'pluginHistory',
        grid: (props: any) => <PluginRecordChangeGrid {...props} />,
    },
    [AuditDataType.PANEL]: {
        query: GET_AUDIT_HISTORY_PANEL,
        response: 'panelHistory',
        grid: (props: any) => <PanelRecordChangeGrid {...props} />,
    },
    [AuditDataType.STUDY]: {
        query: GET_AUDIT_HISTORY_STUDY,
        response: 'studyHistory',
        grid: (props: any) => <StudyRecordChangeGrid {...props} />,
    },
    [AuditDataType.STUDY_SUBJECT_VISIT_SCHEDULE_TEMPLATE]: {
        query: GET_AUDIT_HISTORY_SVT,
        response: 'studySubjectVisitScheduleTemplateHistory',
        grid: (props: any) => <StudySvtRecordChangeGrid {...props} />,
    },
    [AuditDataType.STUDY_COHORT]: {
        query: GET_AUDIT_HISTORY_STUDY_COHORT,
        response: 'studyCohortHistory',
        child: true,
        grid: (props: any) => <StudyCohortRecordChangeGrid {...props} />,
    },
    [AuditDataType.STUDY_CONTROL]: {
        query: GET_AUDIT_HISTORY_STUDY_CONTROL,
        response: 'studyControlHistory',
        grid: (props: any) => <StudyControlRecordChangeGrid {...props} />,
    },
    [AuditDataType.STUDY_TREATMENT_ARM]: {
        query: GET_AUDIT_HISTORY_STUDY_TREATMENT_ARM,
        response: 'studyTreatmentArmHistory',
        child: true,
        grid: (props: any) => <StudyTreatmentArmRecordChangeGrid {...props} />,
    },
    [AuditDataType.STUDY_CRF]: {
        query: GET_AUDIT_HISTORY_STUDY_CRF,
        response: 'studyCrfHistory',
        child: true,
        grid: (props: any) => <StudyCrfRecordChangeGrid {...props} />,
    },
    [AuditDataType.STUDY_MEMBER]: {
        query: GET_AUDIT_HISTORY_STUDY_MEMBER,
        response: 'studyMemberHistory',
        child: true,
        grid: (props: any) => <StudyMemberRecordChangeGrid {...props} />,
    },
    [AuditDataType.ORGANISATION_MEMBER]: {
        query: GET_AUDIT_HISTORY_ORGANISATION_MEMBER,
        response: 'organisationMemberHistory',
        child: true,
        grid: (props: any) => <OrganisationMemberRecordChangeGrid {...props} />,
    },
    [AuditDataType.ORGANISATION_PRODUCT]: {
        query: GET_AUDIT_HISTORY_ORGANISATION_PRODUCT,
        response: 'organisationProductHistory',
        child: true,
        grid: (props: any) => <OrganisationProductRecordChangeGrid {...props} />,
    },
    [AuditDataType.ORGANISATION_PROGRAM]: {
        query: GET_AUDIT_HISTORY_ORGANISATION_PROGRAM,
        response: 'organisationProgramHistory',
        child: true,
        grid: (props: any) => <OrganisationProgramRecordChangeGrid {...props} />,
    },
    [AuditDataType.ORGANISATION_INDICATION]: {
        query: GET_AUDIT_HISTORY_ORGANISATION_INDICATION,
        response: 'organisationIndicationHistory',
        child: true,
        grid: (props: any) => <OrganisationIndicationRecordChangeGrid {...props} />,
    },
    [AuditDataType.RISK_ACTION]: {
        query: GET_AUDIT_HISTORY_RISK_ACTION,
        response: 'riskActionHistory',
        child: true,
        grid: (props: any) => <RiskActionRecordChangeGrid {...props} />,
    },
    [AuditDataType.RISK_OBSERVATION]: {
        query: GET_AUDIT_HISTORY_RISK_OBSERVATION,
        response: 'riskObservationHistory',
        grid: (props: any) => <RiskObservationRecordChangeGrid {...props} />,
    },
    [AuditDataType.RISK_EVALUATION]: {
        query: GET_AUDIT_HISTORY_RISK_EVALUATION,
        response: 'riskEvaluationHistory',
        child: true,
        grid: (props: any) => <RiskEvaluationRecordChangeGrid {...props} />,
    },
    [AuditDataType.RISK_EVALUATION_MODEL]: {
        query: GET_AUDIT_HISTORY_EVALUATION_MODEL,
        response: 'riskEvaluationModelHistory',
        child: true,
        grid: (props: any) => <RiskEvaluationModelRecordChangeGrid {...props} />,
    },
    [AuditDataType.PBI_RISK_REVIEW]: {
        query: null,
        response: null,
        grid: (props: any) => <RiskReviewRecordChangeGrid {...props} />,
    },
    [AuditDataType.REPORT]: {
        query: GET_AUDIT_HISTORY_REPORT,
        response: 'reportHistory',
        grid: (props: any) => <ReportRecordChangeGrid {...props} />,
    },
    [AuditDataType.REPORT_SIGNATORY]: {
        query: GET_AUDIT_HISTORY_REPORT_SIGNATORY,
        response: 'reportSignatoryHistory',
        grid: (props: any) => <ReportSignatoryRecordChangeGrid {...props} />,
    },
    [AuditDataType.CENTRAL_MONITORING_OBSERVATION]: {
        query: GET_AUDIT_HISTORY_CM_OBSERVATION,
        response: 'centralMonitoringObservationHistory',
        grid: (props: any) => <CMObservationRecordChangeGrid {...props} />,
    },
    [AuditDataType.CENTRAL_MONITORING_OBSERVATION_ACTION]: {
        query: GET_AUDIT_HISTORY_CM_ACTION,
        response: 'centralMonitoringActionHistory',
        child: true,
        grid: (props: any) => <CMActionRecordChangeGrid {...props} />,
    },
    [AuditDataType.STUDY_SUBJECT_VISIT_OVER_TIME]: {
        query: GET_AUDIT_HISTORY_STUDY_SUBJECT_VISIT,
        response: 'studySubjectVisitHistory',
        child: true,
        grid: (props: any) => <StudySubjectVisitRecordChangeGrid {...props} />,
    },
    [AuditDataType.DATA_REVIEW_TABLE]: {
        query: GET_AUDIT_HISTORY_DATA_REVIEW_TABLE,
        response: 'dataReviewTableHistory',
        grid: (props: any) => <DataReviewTableRecordChangeGrid {...props} />,
    },
    [AuditDataType.DATA_SET]: {
        query: GET_AUDIT_HISTORY_DATA_SET,
        response: 'dataSetHistory',
        grid: (props: any) => <DataSetRecordChangeGrid {...props} />,
    },
    [AuditDataType.DATA_SET_REVIEW]: {
        query: GET_AUDIT_HISTORY_DATA_SET_REVIEW,
        response: 'dataSetReviewHistory',
        grid: (props: any) => <DataSetReviewRecordChangeGrid {...props} />,
    },
    [AuditDataType.EXTRA_DATA_FIELD]: {
        query: GET_AUDIT_HISTORY_EXTRA_DATA,
        response: 'extraDataHistory',
        grid: (props: any) => <ExtraDataFieldRecordChangeGrid {...props} />,
    },
    [AuditDataType.SITE_REVIEW]: {
        query: GET_AUDIT_HISTORY_SITE_REVIEW,
        response: 'siteReviewHistory',
        child: true,
        grid: (props: any) => <SiteReviewRecordChangeGrid {...props} />,
    },
    [AuditDataType.KRI_REVIEW]: {
        query: GET_AUDIT_LOG_CHANGE_HISTORY,
        response: 'auditLogChangeHistory',
        grid: (props: any) => <AuditLogChangeHistoryGrid {...props} />,
    },
    [AuditDataType.DASHBOARD]: {
        query: GET_AUDIT_HISTORY_STUDY_DASHBOARD_SETUP,
        response: 'dashboardHistory',
        grid: (props: any) => <DashboardSetupChangeGrid {...props} />,
    },
    [AuditDataType.STUDY_ASSESSMENT]: {
        query: GET_AUDIT_HISTORY_STUDY_ASSESSMENT,
        response: 'studyAssessmentHistory',
        child: true,
        grid: (props: any) => <StudyAssessmentRecordChangeGrid {...props} />,
    },
    [AuditDataType.REPORT_DEFINITION]: {
        query: GET_AUDIT_HISTORY_REPORT_DEFINITION,
        response: 'reportDefinitionHistory',
        grid: (props: any) => <ReportDefinitionChangeGrid {...props} />,
    },
    [AuditDataType.STUDY_DASHBOARD_CONFIG]: {
        query: GET_AUDIT_HISTORY_STUDY_DASHBOARD_CONFIG,
        response: 'studyDashboardConfigurationHistory',
        grid: (props: any) => <StudyDashboardConfigAuditLogGrid {...props} />,
    },
    [AuditDataType.STUDY_DASHBOARD_CONFIG_PANEL]: {
        query: GET_AUDIT_HISTORY_STUDY_DASHBOARD_CONFIG_PANEL,
        response: 'studyDashboardConfigPanelHistory',
        grid: (props: any) => <StudyDashboardConfigPanelAuditLogGrid {...props} />,
    },
    [AuditDataType.STUDY_DASHBOARD_CONFIG_WIDGET]: {
        query: GET_AUDIT_HISTORY_STUDY_DASHBOARD_CONFIG_WIDGET,
        response: 'studyDashboardConfigWidgetHistory',
        grid: (props: any) => <StudyDashboardConfigWidgetAuditLogGrid {...props} />,
    },
    [AuditDataType.WIDGET]: {
        query: GET_AUDIT_HISTORY_WIDGET,
        response: 'widgetHistory',
        grid: (props: any) => <WidgetAuditLogGrid {...props} />,
    },
    [AuditDataType.DATA_VIEW_CONFIGURATION]: {
        query: GET_AUDIT_HISTORY_DATA_VIEW_CONFIGURATION,
        response: 'dataViewConfigurationHistory',
        grid: (props: any) => <DataViewConfigurationChangeGrid {...props} />,
    },
    [AuditDataType.DATA_VIEW_FILTER]: {
        query: GET_AUDIT_HISTORY_DATA_VIEW_FILTER,
        response: 'dataViewFilterHistory',
        grid: (props: any) => <DataViewFilterChangeGrid {...props} />,
    },
    [AuditDataType.CM_2_OBSERVATION]: {
        query: GET_AUDIT_HISTORY_CM_OBSERVATION,
        response: 'cm2ObservationHistory',
        grid: (props: any) => <CM2ObservationRecordChangeGrid {...props} />,
    },
    [AuditDataType.CM_2_ACTION]: {
        query: null,
        response: null,
        grid: (props: any) => <CM2ActionRecordChangeGrid {...props} />,
    },
    [AuditDataType.STUDY_HUB_SITE_FIELDS]: {
        query: null,
        response: null,
        grid: (props: any) => <StudyHubSiteFieldsChangeGrid {...props} />,
    },
    [AuditDataType.STUDY_HUB_STUDY_FIELDS]: {
        query: null,
        response: null,
        grid: (props: any) => <StudyHubStudyFieldsChangeGrid {...props} />,
    },
    [AuditDataType.CM_2_REVIEW_LOG]: {
        query: null,
        response: null,
        grid: (props: any) => <CM2ReviewLogRecordChangeGrid {...props} />,
    },
    [AuditDataType.SUBJECT_MONITORING_DATA_LISTINGS_CONFIG]: {
        query: null,
        response: null,
        grid: (props: any) => <SMDataListingsConfigRecordChangeGrid {...props} />,
    },
    [AuditDataType.SUBJECT_MONITORING_SUBJECT_HUB_OVERVIEW_GRID_CONFIG]: {
        query: null,
        response: null,
        grid: (props: any) => <SMOverviewGridConfigRecordChangeGrid {...props} />,
    },
    [AuditDataType.SUBJECT_PROFILE_DETAILS_CONFIGURATION]: {
        query: null,
        response: null,
        grid: (props: any) => <SMSubjectProfileDetailsConfigRecordChangeGrid {...props} />,
    },
    [AuditDataType.LIBRARY_FUNCTIONAL_DATA_REVIEW]: {
        query: GET_AUDIT_HISTORY_LIBRARY_FUNCTIONAL_DATA_REVIEW,
        response: 'libraryFunctionalDataReviewHistory',
        grid: (props: any) => <LibraryFunctionDataReviewChangeGrid {...props} />,
    },
    [AuditDataType.TAG]: {
        query: null,
        response: null,
        grid: (props: any) => <Tag {...props} />,
    },
    [AuditDataType.TAG_INSTANCE]: {
        query: null,
        response: null,
        grid: (props: any) => <SmTagInstance {...props} />,
    },
    [AuditDataType.CONTROLS_HUB_CONFIG_WIDGET]: {
        query: GET_AUDIT_HISTORY_CONTROLS_HUB_CONFIG_WIDGET,
        response: 'controlsHubConfigWidgetHistory',
        grid: (props: any) => <ControlsHubConfigWidgetAuditLogGrid {...props} />,
    },
    [AuditDataType.CONTROLS_HUB_WIDGET]: {
        query: GET_AUDIT_HISTORY_CONTROLS_HUB_WIDGET,
        response: 'controlsHubWidgetHistory',
        grid: (props: any) => <ControlsHubWidgetAuditLogGrid {...props} />,
    },
    [AuditDataType.CONTROLS_HUB_CONFIG_PANEL]: {
        query: GET_AUDIT_HISTORY_CONTROLS_HUB_CONFIG_PANEL,
        response: 'controlsHubConfigPanelHistory',
        grid: (props: any) => <ControlsHubConfigPanelAuditLogGrid {...props} />,
    },
    [AuditDataType.SUBJECT_DATA_LISTING_REVIEW]: {
        query: null,
        response: null,
        grid: (props: any) => <SubjectDataListingReviewInstances {...props} />,
    },
    [AuditDataType.SUBJECT_MONITORING_REVIEW]: {
        query: null,
        response: null,
        grid: (props: any) => <SmReviews {...props} />,
    },
    [AuditDataType.SUBJECT_REVIEW_INSTANCE]: {
        query: null,
        response: null,
        grid: (props: any) => <SubjectReviewInstances {...props} />,
    },
    [AuditDataType.SUBJECT_OBSERVATIONS]: {
        query: null,
        response: null,
        grid: (props: any) => <SubjectObservationsAuditLogGrid {...props} />,
    },
    [AuditDataType.SUBJECT_ACTIONS]: {
        query: null,
        response: null,
        grid: (props: any) => <SubjectActionsAuditLogGrid {...props} />,
    },
    [AuditDataType.SUBJECT_REVIEW_LOGS]: {
        query: null,
        response: null,
        grid: (props: any) => <SubjectReviewLogsAuditLogGrid {...props} />,
    },
    [AuditDataType.SUBJECT_MONITORING_REVIEW_PLAN]: {
        query: null,
        response: null,
        grid: (props: any) => <SMReviewPlanRecordChangeGrid {...props} />,
    },
    [AuditDataType.SUBJECT_MONITORING_REVIEW_TYPE]: {
        query: null,
        response: null,
        grid: (props: any) => <SMReviewType {...props} />,
    },
    [AuditDataType.RISK_DASHBOARD_SETUP]: {
        query: null,
        response: null,
        grid: (props: any) => <RiskDashboardSetupGrid {...props} />,
    },
    [AuditDataType.RISK_ASSESSMENT_TAG]: {
        query: null,
        response: null,
        grid: (props: any) => <RiskAssessmentTag {...props} />,
    },
    [AuditDataType.RISK_COMMENT]: {
        query: GET_AUDIT_HISTORY_RISK_COMMENT,
        response: 'riskCommentHistory',
        grid: (props: any) => <Comment {...props} />,
    },
    [AuditDataType.RISK_COMMENT_REPLY]: {
        query: GET_AUDIT_HISTORY_RISK_COMMENT_REPLY,
        response: 'riskCommentReplyHistory',
        grid: (props: any) => <Comment {...props} />,
    },
    [AuditDataType.CTQ_COMMENT]: {
        query: GET_AUDIT_HISTORY_CTQ_COMMENT,
        response: 'ctqCommentHistory',
        grid: (props: any) => <Comment {...props} />,
    },
    [AuditDataType.CTQ_COMMENT_REPLY]: {
        query: GET_AUDIT_HISTORY_CTQ_COMMENT_REPLY,
        response: 'ctqCommentReplyHistory',
        grid: (props: any) => <Comment {...props} />,
    },
};

export default AuditItemTypes;
